import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { GcsFile } from '@aid/shared/types/classes';

@Component({
  selector: 'aid-upload-multiple-files',
  template: `
    <div class="aid-layout-row-wrap">
      @if (files.length > 0) {
        @for (file of files; track file) {
          <div
            class="border border-radius-8 pointer damage__attach-file aid-layout-column aid-layout-align-space-between-center"
            (click)="onViewFile(file)"
          >
            <div
              class="full-width full-height aid-layout-row aid-layout-align-center-center"
            >
              <mat-icon
                [svgIcon]="fileTypeIcon(file)"
                class="damage__type-icon"
              ></mat-icon>
            </div>
            <div
              class="damage--border-top padding-8 full-width aid-layout-row aid-layout-align-space-between-center aid-layout-gap-16"
            >
              <span class="mat-small damage--break-word">
                {{ file.fileName }}
              </span>
              @if (!disabled) {
                <button
                  type="button"
                  mat-icon-button
                  class="damage__delete"
                  (click)="onDelete(file); $event.stopPropagation()"
                >
                  <mat-icon
                    svgIcon="delete"
                    class="damage__delete--icon"
                  ></mat-icon>
                </button>
              }
            </div>
          </div>
        }
      }
      @if (disabled && files.length === 0) {
        <span class="mat-small">{{ 'no-files-attached' | translate }}</span>
      }
      @if (viewAdd) {
        <div
          class="border-dashed border-radius-8 pointer damage__attach-file aid-layout-column aid-layout-align-center-center"
          (click)="onFile()"
          (drop)="onAddFileByDropping($event.dataTransfer?.files)"
        >
          <mat-icon
            svgIcon="add"
            color="primary"
            class="damage__type-icon"
          ></mat-icon>
        </div>
      }
    </div>

    @if (control.hasError('required')) {
      <mat-error>
        {{ 'upload-file-required' | translate | sentancecase }}
      </mat-error>
    }

    <input
      type="file"
      #file
      style="display: none"
      (change)="onAddFileBySelecting(file.files)"
      [accept]="acceptFiles"
      multiple
    />
  `,
  styleUrls: ['./upload-multiple-files.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class UploadMultipleFilesComponent implements OnInit {
  @Input('control') set _control(control: UntypedFormControl) {
    this.control = control;
    if (this.control.value) {
      this.files = this.control.value;
    }
  }
  @Input() acceptFiles = '.jpg, .jpeg, .png';
  @Input() disabled = false;
  @Input() singleFile = false;

  @Output() viewFile = new EventEmitter<GcsFile>();

  files: GcsFile[] = [];
  localLinks: { [id: string]: string } = {};

  @ViewChild('file') file?: ElementRef<HTMLInputElement>;

  typeIconMap: { [fileType: string]: string } = {
    'application/pdf': 'pdf_file',
    'image/jpeg': 'jpg_file',
    'image/jpg': 'jpg_file',
    'image/png': 'png_file'
  };
  control: UntypedFormControl = new UntypedFormControl();

  constructor() {}

  ngOnInit() {
    this.control.valueChanges.subscribe(files => {
      if (files.length > 0) {
        this.control.markAsDirty();
      }

      this.files = files ? files : [];
    });
  }

  onFile() {
    this.fileInput.click();
  }

  onAddFileByDropping(droppedFiles: FileList | undefined) {
    if (!droppedFiles) {
      return;
    }
    const filesOfCorrectType: File[] = [];

    const arrayOfAllowedTypes = this.acceptFiles.replace(/\s/g, '').split(',');

    for (let i = 0; i < droppedFiles.length; i++) {
      const file = droppedFiles[i];
      if (arrayOfAllowedTypes.some(fileType => file.name.endsWith(fileType))) {
        filesOfCorrectType.push(file);
      }
    }

    if (filesOfCorrectType.length > 0) {
      this.processFiles(filesOfCorrectType);
    }
  }

  onAddFileBySelecting(files: FileList | null) {
    if (files) {
      this.processFiles(files);
    }
  }

  processFiles(filesList: FileList | File[]) {
    Array.from(filesList).map((value: File) => {
      const file = new GcsFile();
      file.fileName = value.name;
      file.fileType = value.type;
      file.file = value;
      this.files.push(file);

      this.localLinks[`${value.name}-${value.type}`] = URL.createObjectURL(
        value
      );
    });

    const files = [...this.files];
    this.control.patchValue(files);
    this.fileInput.value = '';
  }

  fileTypeIcon(file: GcsFile): string {
    return (file.fileType && this.typeIconMap[file.fileType]) || '';
  }

  onViewFile(value: GcsFile) {
    if (!value.id && value.file) {
      window.open(this.localLinks[`${value.fileName}-${value.fileType}`]);
      return;
    }
    this.viewFile.emit(value);
  }

  onDelete(file: GcsFile) {
    const index = this.files.findIndex(
      value =>
        value.fileType === file.fileType && value.fileName === file.fileName
    );

    const files = [...this.files];
    files.splice(index, 1);
    this.control.patchValue(files);
  }

  get viewAdd() {
    if (this.disabled) {
      return false;
    }

    return !(this.singleFile && this.files.length === 1);
  }

  private get fileInput(): HTMLInputElement {
    if (!this.file) {
      throw new Error(
        'aid-upload-multiple-files: the file input is not rendered.'
      );
    }
    return this.file.nativeElement;
  }
}
